import { useEffect, useState } from 'react';
import { useGame } from '../../game/store';
import { useSettings } from '../../stores/settings-store';
import { useGameInstance } from './GameContext';

export function Toasts() {
  const toasts = useGame((s) => s.toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function AreaBanner() {
  const area = useGame((s) => s.area);
  if (!area || Date.now() - area.at > 4000) return null;
  return (
    <div className="area-banner" key={area.at}>
      <div className="big">{area.name}</div>
      <div className="line" />
    </div>
  );
}

export function InteractHint() {
  const hint = useGame((s) => s.interact);
  const game = useGameInstance();
  if (!hint) return null;
  return (
    <button className="interact-hint" onClick={() => game?.triggerAction('interact')}>
      <kbd>{hint.key}</kbd>
      {hint.label}
    </button>
  );
}

export function DeathOverlay() {
  const self = useGame((s) => s.self);
  const game = useGameInstance();
  const [now, setNow] = useState(Date.now());
  const dead = !!self?.dead;
  useEffect(() => {
    if (!dead) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [dead]);
  if (!self?.dead) return null;
  const wait = Math.max(0, 3 - (now - self.deadAt) / 1000);
  return (
    <div className="death-overlay" role="alertdialog" aria-labelledby="death-title">
      <h2 id="death-title">Defeated</h2>
      <p className="muted">Even masters fall. Rise again at Willow Creek.</p>
      <button className="btn btn-primary" disabled={wait > 0} onClick={() => game?.respawn()} autoFocus>
        {wait > 0 ? `Recovering… ${Math.ceil(wait)}` : 'Return to the village'}
      </button>
    </div>
  );
}

export function ConnectionBanner() {
  const c = useGame((s) => s.connection);
  const loading = useGame((s) => s.loading);
  if (loading || c === 'online' || c === 'offline') return null;
  return (
    <div className="conn-banner">
      {c === 'reconnecting' ? 'Connection lost — reconnecting…' : 'Connecting…'}
    </div>
  );
}

export function FpsCounter() {
  const show = useSettings((s) => s.showFps);
  const fps = useGame((s) => s.fps);
  const ping = useGame((s) => s.ping);
  const game = useGameInstance();
  if (!show || !game) return null;
  const info = game.rendererInfo();
  return (
    <div className="fps-counter">
      {`${fps} fps · ${ping} ms\n${info.calls} draws · ${(info.triangles / 1000).toFixed(0)}k tris\n${info.quality} · ${info.entities} ents · ${info.effects} fx`}
    </div>
  );
}

const KICK_TEXT: Record<string, string> = {
  duplicate_login: 'This character was logged in from another window or device.',
  logged_out: 'You logged out.',
  server_shutdown: 'The server is restarting. Please try again in a moment.',
  connection_lost: 'The connection to the server was lost.',
  character_not_found: 'That character could not be found.',
  protocol_violation: 'Disconnected: invalid data was sent to the server.',
  join_failed: 'Could not enter the world.',
};

export function KickOverlay({ onLeave }: { onLeave: () => void }) {
  const kicked = useGame((s) => s.kicked);
  const connection = useGame((s) => s.connection);
  if (!kicked || connection !== 'offline') return null;
  return (
    <div className="kick-overlay" role="alertdialog">
      <h2 className="gold" style={{ fontFamily: 'var(--font-display)' }}>
        Disconnected
      </h2>
      <p className="muted">{KICK_TEXT[kicked] ?? `Disconnected (${kicked}).`}</p>
      <button className="btn btn-primary" onClick={onLeave} autoFocus>
        Back to character select
      </button>
    </div>
  );
}

export function LoadingVeil() {
  const loading = useGame((s) => s.loading);
  const c = useGame((s) => s.connection);
  if (!loading) return null;
  return (
    <div className="loading-veil" role="status">
      <div className="spinner" />
      <div className="muted">{c === 'reconnecting' ? 'Reconnecting…' : 'Entering the martial world…'}</div>
    </div>
  );
}
